/**
 * API клиент для Web App АИша-Диетолог
 *
 * Взаимодействие с backend через axios
 * Аутентификация через Telegram WebApp initData
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type {
  ApiResponse,
  OnboardingData,
  OnboardingResponse,
  OnboardingStatusPayload,
  MealPlan,
  GenerateMealPlanRequest,
  AdaptRecipeRequest,
  AdaptRecipeResponse,
  FromFridgeRequest,
  AnalyzePhotoRequest,
  AnalyzePhotoResponse,
  SubscriptionStatusPayload,
  SubscriptionPlansPayload,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  UserProfile,
  UpdateProfileRequest,
  UserProgress,
  FoodLogEntry,
  LogMealRequest,
  DailyFoodTotals,
  FoodLogStreak,
  WaterLog,
  AddWaterRequest,
  ApiError,
  PdfSpecialistInfo,
  CreatePdfPaymentRequest,
  RegisterSpecialistRequest,
  RegisterSpecialistPayload,
  CreatePdfPaymentPayload,
} from '../types/api';

// ============= КОНФИГУРАЦИЯ =============

// Base URL из переменных окружения или production URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vitafort.ru/api';

// Кэш initData: на мобильном Telegram диалог разрешения микрофона может сбросить
// window.Telegram.WebApp.initData в пустую строку, из-за чего следующий запрос
// (голосовая транскрипция) уходит без авторизации и получает 401.
// Кэшируем initData один раз при первой доступности — это безопасно, так как
// initData подписан Telegram и содержит auth_date, защищающий от replay-атак.
let _telegramInitDataCache: string | null = null;

// Timeout для запросов (30 секунд)
const API_TIMEOUT = 30000;

// Максимальное количество попыток retry
const MAX_RETRY_ATTEMPTS = 3;

// Экран /paid сам опрашивает сервер и сам пишет «нет связи» — системные всплывашки
// там сыпались бы каждые 4 секунды
const silentNetworkAlerts = (): boolean => window.location.pathname.endsWith('/paid');

/** sessionStorage: email-сессию этой вкладки стёр сервер (истекла / аккаунт удалён) */
export const SESSION_EXPIRED_FLAG = 'aisha_session_expired';

/**
 * В2: скользящая email-сессия. Сервер присылает новый ключ в X-Session-Renew, когда
 * старому осталось меньше 150 дней, — кто заходит хотя бы раз в полгода, не вылетает.
 * Сохраняем, только если запрос ушёл с тем же ключом, что и сейчас в браузере:
 * иначе ответ, пришедший после «Выйти», вернул бы сессию обратно.
 */
function applySessionRenew(responseHeaders: unknown, requestHeaders: unknown): void {
  try {
    const h = responseHeaders as any;
    const renew = String((typeof h?.get === 'function' ? h.get('x-session-renew') : h?.['x-session-renew']) || '');
    if (!renew) return;
    const sent = String((requestHeaders as any)?.Authorization || '');
    const current = localStorage.getItem('aisha_session_key');
    if (current && sent === `Bearer ${current}`) {
      localStorage.setItem('aisha_session_key', renew);
    }
  } catch { /* приватный режим — продлим в другой раз */ }
}

// ============= AXIOS INSTANCE =============

/**
 * Создание настроенного axios клиента
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - добавляем аутентификацию
  client.interceptors.request.use(
    (config) => {
      // Получаем данные Telegram WebApp
      const tg = window.Telegram?.WebApp;

      // Кэшируем initData при первой доступности (защита от сброса при диалоге микрофона)
      if (tg?.initData) {
        _telegramInitDataCache = tg.initData;
      }
      const resolvedInitData = tg?.initData || _telegramInitDataCache;

      if (resolvedInitData) {
        // Telegram-авторизация
        config.headers['X-Telegram-Init-Data'] = resolvedInitData;
      } else {
        // Браузерный вход — используем email session_key
        const sessionKey = localStorage.getItem('aisha_session_key');
        if (sessionKey) {
          config.headers['Authorization'] = `Bearer ${sessionKey}`;
        }
      }

      // Добавляем user ID если есть
      if (tg?.initDataUnsafe?.user?.id) {
        config.headers['X-Telegram-User-Id'] = tg.initDataUnsafe.user.id.toString();
      }

      // Логируем запрос в dev режиме
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.data);
      }

      return config;
    },
    (error) => {
      console.error('[API] Request error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - обработка ответов и ошибок
  client.interceptors.response.use(
    (response) => {
      applySessionRenew(response.headers, response.config?.headers);
      // Логируем ответ в dev режиме
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API] Response:`, response.data);
      }

      return response;
    },
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: number };
      if (error.response) applySessionRenew(error.response.headers, error.config?.headers);

      // Логируем ошибку
      console.error('[API] Response error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });

      // Обработка различных ошибок
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401: {
            console.error('[API] Authentication error — initData missing or expired');
            // Голосовые запросы могут получить транзиентный 401 на мобильном Telegram:
            // диалог разрешения микрофона сбрасывает WebApp-контекст, инициализируется
            // заново и в этот момент initData временно пуст. Не сбрасываем сессию и не
            // редиректим — компонент сам покажет понятную ошибку. Кэш initData
            // (_telegramInitDataCache) должен закрыть этот кейс, но оставляем защиту.
            const failedUrl = error.config?.url || '';
            const isVoiceRequest = failedUrl.includes('/stt/voice');
            // Если у пользователя НЕТ ни email-сессии ни TG initData —
            // 401 ожидаем (quiz-funnel, онбординг до регистрации).
            // Не стреляем aisha:unauthorized, иначе пользователя выкидывает
            // на welcome прямо во время заполнения анкеты.
            const hadEmailSession = !!localStorage.getItem('aisha_session_key');
            const hadTgAuth = !!_telegramInitDataCache;
            // F3: email-сессию стираем, только если сервер прямо сказал, что она
            // недействительна (тексты закреплены в tests/test_auth_integration.py).
            // Раньше любой 401 (в т.ч. от одного обработчика) выкидывал из аккаунта
            // прямо на /paid после оплаты.
            const sentBearer = String(
              (error.config?.headers as any)?.Authorization || ''
            ).startsWith('Bearer ');
            const errText = String((data as any)?.error || (data as any)?.message || '');
            const errCode = String((data as any)?.code || '');
            const sessionInvalid =
              errCode === 'SESSION_EXPIRED' ||
              errCode === 'ACCOUNT_DELETED' ||
              /session expired|account deleted/i.test(errText);
            const wasLoggedIn = sentBearer ? sessionInvalid : (hadEmailSession || hadTgAuth);
            if (!isVoiceRequest && wasLoggedIn) {
              // Очищаем все aisha_* ключи кэша (тот же список что в SettingsScreen)
              [
                'aisha_brief', 'aisha_created_at', 'aisha_streak',
                'aisha_track_visited', 'aisha_recipe_visited',
                'aisha_onboarding_form', 'aisha_profile_done',
              ].forEach(k => localStorage.removeItem(k));
              // Очищаем email-сессию, только если именно она отвергнута
              if (hadEmailSession && sentBearer) {
                localStorage.removeItem('aisha_session_key');
                localStorage.removeItem('aisha_email_user_id');
                // /paid и /subscription покажут «вход устарел», а не «тебя не знаем»
                try { sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1'); } catch { /* ignore */ }
              }
              // Диспатчим глобальный event — App.tsx поймает и сделает редирект.
              window.dispatchEvent(new CustomEvent('aisha:unauthorized'));
            }
            break;
          }

          case 403:
            // Forbidden — подписка или окончание триала плана (E1).
            // НЕ делаем жёсткий редирект: PlanScreen сам обрабатывает TRIAL_ENDED
            // через { phase: 'paywall' }. Редирект здесь обходил бы in-screen пейволл.
            console.warn('[API] 403 received, code:', data?.code);
            break;

          case 429:
            // Too Many Requests - rate limit
            console.warn('[API] Rate limit exceeded');
            if (isJobPoll(error.config?.url)) break;
            window.Telegram?.WebApp?.showAlert?.(
              'Слишком много запросов. Подождите немного и попробуйте снова.'
            );
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors - пробуем retry
            if (!originalRequest._retry) {
              originalRequest._retry = 0;
            }

            if (originalRequest._retry < MAX_RETRY_ATTEMPTS) {
              originalRequest._retry++;
              console.log(`[API] Retrying request (${originalRequest._retry}/${MAX_RETRY_ATTEMPTS})`);

              // Экспоненциальная задержка перед retry
              const delay = Math.pow(2, originalRequest._retry) * 1000;
              await new Promise(resolve => setTimeout(resolve, delay));

              return client.request(originalRequest);
            }
            break;
        }
      } else if (
        error.code === 'ECONNABORTED' ||
        (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'))
      ) {
        console.error('[API] Request timeout');
        if (!silentNetworkAlerts() && !isJobPoll(error.config?.url)) {
          window.Telegram?.WebApp?.showAlert?.(
            'Сервер отвечает дольше обычного. Подождите и попробуйте снова.'
          );
        }
      } else if (error.request) {
        // Запрос был отправлен, но ответа не было (сетевая ошибка)
        console.error('[API] Network error');
        if (!silentNetworkAlerts() && !isJobPoll(error.config?.url)) {
          window.Telegram?.WebApp?.showAlert?.(
            'Ошибка сети. Проверьте подключение к интернету.'
          );
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Создаем единственный экземпляр клиента
const apiClient = createApiClient();

// Публичный клиент для эндпоинтов без Telegram-авторизации (PDF ref, download)
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

publicApiClient.interceptors.request.use((config) => {
  const tg = window.Telegram?.WebApp;
  if (tg?.initData) {
    config.headers['X-Telegram-Init-Data'] = tg.initData;
  }
  if (tg?.initDataUnsafe?.user?.id) {
    config.headers['X-Telegram-User-Id'] = tg.initDataUnsafe.user.id.toString();
  }
  return config;
});

publicApiClient.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const orig = error.config as any;
    const status = error.response?.status;
    if (status && [500, 502, 503, 504].includes(status)) {
      orig._retry = (orig._retry || 0) + 1;
      if (orig._retry <= 2) {
        await new Promise(r => setTimeout(r, Math.pow(2, orig._retry) * 1000));
        return publicApiClient.request(orig);
      }
    }
    return Promise.reject(error);
  }
);

// ============= ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ =============

/**
 * Обертка для безопасной обработки API запросов
 */
async function handleApiRequest<T>(
  request: Promise<any>
): Promise<ApiResponse<T>> {
  try {
    const response = await request;
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ApiError>;

    return {
      success: false,
      error: axiosError.response?.data?.message || axiosError.message || 'Unknown error',
      data: axiosError.response?.data as any,
      status: axiosError.response?.status,
      networkError: !axiosError.response,
    };
  }
}

/** Опрос фоновой AI-задачи — без системных всплывашек: сбой опроса повторяем молча */
const isJobPoll = (url?: string): boolean => !!url && url.startsWith('/jobs/');

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * №64 фаза A: долгий AI-запрос фоновой задачей.
 * POST с X-Async-Job: 1 → 202 {job_id} → опрос GET /jobs/{id} до готовности.
 * Ни один HTTP-запрос не длится дольше секунд (лимит Cloudflare 100 с → 524).
 * Результат — в том же формате, что handleApiRequest: экраны не меняются.
 * Если сервер ответил сразу (старый бэкенд без фоновых задач) — отдаём ответ как есть.
 */
async function runAsJob<T>(path: string, body: unknown, timeoutMs: number): Promise<ApiResponse<T>> {
  let startResponse;
  try {
    startResponse = await apiClient.post(path, body, {
      timeout: timeoutMs,
      headers: { 'X-Async-Job': '1' },
    });
  } catch (error) {
    return handleApiRequest<T>(Promise.reject(error));
  }

  const jobId: string | undefined = startResponse.data?.job_id;
  if (startResponse.status !== 202 || !jobId) {
    return { success: true, data: startResponse.data };
  }

  const startedAt = Date.now();
  let networkFails = 0;
  while (Date.now() - startedAt < timeoutMs) {
    // первые 30 с чаще: короткие задачи (разбор еды) не ждут лишнего
    await sleep(Date.now() - startedAt < 30000 ? 2000 : 3000);
    let job: any;
    try {
      job = (await apiClient.get(`/jobs/${jobId}`, { timeout: 15000 })).data;
      networkFails = 0;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      const status = axiosError.response?.status;
      if (!axiosError.response) {
        // VPN/сеть моргнула — задача на сервере продолжается, спросим ещё раз
        if (++networkFails < 5) continue;
        return handleApiRequest<T>(Promise.reject(error));
      }
      if (status === 429) continue;
      if (status === 404) {
        // Сервер перезапускался — задача потерялась
        return {
          success: false,
          error: 'Связь с сервером прервалась. Попробуй ещё раз 🙂',
          data: {
            success: false,
            code: 'JOB_LOST',
            error: 'Связь с сервером прервалась. Попробуй ещё раз 🙂',
          } as any,
          status: 404,
          networkError: false,
        };
      }
      return handleApiRequest<T>(Promise.reject(error));
    }

    if (job?.status !== 'done') continue;
    const httpStatus: number = job.http_status;
    const payload = job.body;
    if (httpStatus >= 200 && httpStatus < 300) {
      return { success: true, data: payload };
    }
    return {
      success: false,
      error: payload?.message || `Request failed with status code ${httpStatus}`,
      data: payload,
      status: httpStatus,
      networkError: false,
    };
  }

  return {
    success: false,
    error: 'timeout of ' + timeoutMs + 'ms exceeded',
    status: undefined,
    networkError: true,
  };
}

// ============= API МЕТОДЫ =============

/**
 * API для работы с онбордингом
 */
// Формат payload, который ждёт backend POST /api/onboarding
// (см. services/api_server.py::submit_onboarding).
// Поля snake_case, goal/activity_level в строковом виде бота.
export interface OnboardingRestPayload {
  age: number;
  gender: 'male' | 'female';
  height: number;
  weight: number;
  goal: 'weight_loss' | 'maintenance' | 'muscle_gain' | 'medical';
  activity_level: 'minimal' | 'low' | 'medium' | 'high' | 'very_high';
  health_conditions: string[];
  dietary_restrictions: string[];
  additional_info?: string;
  /** Подтверждённый целевой вес (кг). Влияет на расчёт калорий на бэке (мягкий дефицит 10%). */
  target_weight_kg?: number;
  /** Препараты (чипы анкеты) — уходят в клинический контекст AI через additional_info. */
  medications?: string[];
  timezone?: string;
  language?: string;
  pdp_consent?: boolean;
  health_data_consent?: boolean;
}

const onboardingApi = {
  /**
   * Отправить данные анкеты (camelCase-формат, старый путь).
   * Оставлен для обратной совместимости, новые экраны используют submitOnboarding().
   */
  async sendOnboarding(data: OnboardingData): Promise<ApiResponse<OnboardingResponse>> {
    return handleApiRequest<OnboardingResponse>(
      apiClient.post('/onboarding', data)
    );
  },

  /**
   * Сохранить анкету через REST — работает из любого входа в webapp
   * (Menu Button / inline / reply). В отличие от Telegram.WebApp.sendData(),
   * который работает только из reply-кнопки, REST требует только валидный
   * initData (его добавляет axios-интерцептор).
   *
   * Возвращает calories_target / protein_target, но мы их не используем:
   * расчёт на фронте делает computeNutrition() (зеркало utils/helpers.py).
   * REST нужен исключительно для сохранения профиля в БД.
   */
  async submitOnboarding(data: OnboardingRestPayload): Promise<ApiResponse<unknown>> {
    return handleApiRequest(
      apiClient.post('/onboarding', data, {
        // Онбординг идёт без AI, укладывается в 10 секунд за глаза.
        timeout: 15000,
      })
    );
  },

  /**
   * Проверить, прошел ли пользователь онбординг
   */
  async checkOnboardingStatus(): Promise<ApiResponse<OnboardingStatusPayload>> {
    return handleApiRequest(
      apiClient.get('/onboarding/status')
    );
  },
};

/**
 * API для работы с планами питания
 */
const mealPlanApi = {
  /**
   * Получить список планов питания
   */
  async getMealPlans(params?: {
    limit?: number;
    offset?: number;
    status?: 'active' | 'completed' | 'cancelled';
  }): Promise<ApiResponse<MealPlan[]>> {
    return handleApiRequest<MealPlan[]>(
      apiClient.get('/meal-plans', { params })
    );
  },

  /**
   * Получить план питания по ID
   */
  async getMealPlan(planId: string): Promise<ApiResponse<MealPlan>> {
    return handleApiRequest<MealPlan>(
      apiClient.get(`/meal-plans/${planId}`)
    );
  },

  /**
   * Получить текущий активный план
   */
  async getCurrentMealPlan(): Promise<ApiResponse<MealPlan>> {
    return handleApiRequest<MealPlan>(
      apiClient.get('/meal-plans/current')
    );
  },

  /**
   * Сгенерировать новый план питания
   */
  async generateMealPlan(request: GenerateMealPlanRequest): Promise<ApiResponse<MealPlan>> {
    // Сложный профиль (лекарства, исключения) — DeepSeek до 4–5 минут.
    // Идёт фоновой задачей (№64): HTTP-запросы короткие, ждём готовности до 6 минут.
    return runAsJob<MealPlan>('/meal-plans/generate', request, 360000);
  },

  /**
   * Обновить статус плана
   */
  async updateMealPlanStatus(
    planId: string,
    status: 'active' | 'completed' | 'cancelled'
  ): Promise<ApiResponse<MealPlan>> {
    return handleApiRequest<MealPlan>(
      apiClient.patch(`/meal-plans/${planId}/status`, { status })
    );
  },
};

/**
 * API для адаптации рецептов
 */
const recipeApi = {
  /**
   * Адаптировать рецепт под требования
   */
  async adaptRecipe(request: AdaptRecipeRequest): Promise<ApiResponse<AdaptRecipeResponse>> {
    // Рецепты через AI часто отвечают дольше стандартного timeout — фоновой задачей
    return runAsJob<AdaptRecipeResponse>('/recipes/adapt', request, 120000);
  },

  /**
   * Придумать блюдо из продуктов, которые есть у пользователя.
   * Тот же backend-паттерн, что и adaptRecipe: общий лимит recipe_adaptations,
   * код ошибки LIMIT_EXCEEDED → экран оплаты.
   */
  async generateFromFridge(request: FromFridgeRequest): Promise<ApiResponse<AdaptRecipeResponse>> {
    return runAsJob<AdaptRecipeResponse>('/recipes/from-fridge', request, 120000);
  },

  /**
   * Получить альтернативы ингредиента
   * Backend ожидает POST /api/recipes/alternatives
   */
  async getIngredientAlternatives(ingredient: string): Promise<ApiResponse<string[]>> {
    return handleApiRequest<string[]>(
      apiClient.post('/recipes/alternatives', { ingredient })
    );
  },
};

/**
 * API для анализа фото
 */
const photoApi = {
  /**
   * Анализировать фото еды
   */
  async analyzePhoto(request: AnalyzePhotoRequest): Promise<ApiResponse<AnalyzePhotoResponse>> {
    return handleApiRequest<AnalyzePhotoResponse>(
      apiClient.post('/photos/analyze', request, {
        timeout: 90000, // 90 секунд для обработки фото
      })
    );
  },

  /**
   * Получить историю анализов фото
   */
  async getPhotoHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<AnalyzePhotoResponse[]>> {
    return handleApiRequest<AnalyzePhotoResponse[]>(
      apiClient.get('/photos/history', { params })
    );
  },
};

/**
 * API для дневника питания
 */
const foodLogApi = {
  /**
   * Добавить запись в дневник
   */
  async logMeal(request: LogMealRequest): Promise<ApiResponse<FoodLogEntry>> {
    const source =
      request.sourceKind === 'photo' || request.imageBase64
        ? 'photo'
        : request.sourceKind === 'voice'
          ? 'voice'
          : request.sourceKind === 'text'
            ? 'text'
            : 'manual';
    const payload = {
      description: request.dishName,
      calories: request.calories,
      protein: request.protein,
      fat: request.fat,
      carbs: request.carbs,
      meal_type: request.mealType,
      timestamp: request.timestamp,
      source,
      client_request_id: request.clientRequestId,
      analysis_text: request.analysisText,
      analysis: {
        dish_name: request.dishName,
        calories: request.calories,
        protein: request.protein,
        fat: request.fat,
        carbs: request.carbs,
        meal_type: request.mealType,
        client_request_id: request.clientRequestId,
      },
    };
    return handleApiRequest<FoodLogEntry>(
      apiClient.post('/food-log', payload)
    );
  },

  /**
   * Получить записи дневника за день
   */
  async getDailyLog(date: string): Promise<ApiResponse<FoodLogEntry[]>> {
    return handleApiRequest<FoodLogEntry[]>(
      apiClient.get('/food-log/daily', {
        params: { date },
      })
    );
  },

  /**
   * Получить суммарные КБЖУ за день
   */
  async getDailyTotals(date?: string): Promise<ApiResponse<DailyFoodTotals>> {
    return handleApiRequest<DailyFoodTotals>(
      apiClient.get('/food-log/totals', {
        params: { date },
      })
    );
  },

  /**
   * Получить записи за период
   */
  async getLogRange(days: number = 7): Promise<ApiResponse<any>> {
    return handleApiRequest(
      apiClient.get('/food-log/range', {
        params: { days },
      })
    );
  },

  async analyzeMeal(request: any): Promise<ApiResponse<any>> {
    return runAsJob('/food-log/analyze', request, 60000);
  },

  async getStreak(): Promise<ApiResponse<FoodLogStreak>> {
    return handleApiRequest<FoodLogStreak>(apiClient.get('/food-log/streak'));
  },

  async getWeeklySummary(): Promise<ApiResponse<any>> {
    return handleApiRequest(apiClient.get('/food-log/weekly-summary'));
  },

  /**
   * Удалить запись из дневника
   */
  async deleteLogEntry(entryId: string | number): Promise<ApiResponse<{ success: boolean }>> {
    return handleApiRequest(
      apiClient.delete(`/food-log/${entryId}`)
    );
  },

  async submitAnalysisFeedback(
    entryId: string | number,
    reaction: 'positive' | 'neutral' | 'negative',
    comment?: string
  ): Promise<ApiResponse<any>> {
    return handleApiRequest(
      apiClient.post('/food-log/feedback', {
        related_id: entryId,
        reaction,
        comment,
      })
    );
  },
};

/**
 * API для трекера воды
 */
const waterApi = {
  /**
   * Добавить воду
   */
  async addWater(request: AddWaterRequest): Promise<ApiResponse<WaterLog>> {
    return handleApiRequest<WaterLog>(
      apiClient.post('/water', request)
    );
  },

  /**
   * Получить данные трекера за день
   */
  async getDailyWater(date?: string): Promise<ApiResponse<WaterLog>> {
    return handleApiRequest<WaterLog>(
      apiClient.get('/water/daily', {
        params: { date },
      })
    );
  },

  /**
   * Обновить цель по воде
   */
  async updateWaterGoal(goalMl: number): Promise<ApiResponse<{ success: boolean }>> {
    return handleApiRequest(
      apiClient.patch('/water/goal', { goalMl })
    );
  },

  /**
   * Получить статистику по воде за период
   */
  async getWaterStats(startDate: string, endDate: string): Promise<ApiResponse<any>> {
    return handleApiRequest(
      apiClient.get('/water/stats', {
        params: { startDate, endDate },
      })
    );
  },
};

/**
 * API для подписок
 */
const subscriptionApi = {
  /**
   * Получить статус подписки
   */
  async getSubscriptionStatus(): Promise<ApiResponse<SubscriptionStatusPayload>> {
    return handleApiRequest<SubscriptionStatusPayload>(apiClient.get('/subscription/status'));
  },

  /**
   * Создать подписку (YooKassa). Бэкенд: snake_case; здесь — unwrap в плоский data (camelCase).
   */
  async createSubscription(
    request: CreateSubscriptionRequest
  ): Promise<ApiResponse<CreateSubscriptionResponse>> {
    const body: Record<string, unknown> = {
      planId: request.planId,
      tariff: request.tariff ?? request.planId,
      returnUrl: request.returnUrl,
      return_url: request.returnUrl,
      paymentMethod: request.paymentMethod,
    };
    if (request.email) {
      body['email'] = request.email;
    }
    const r = await handleApiRequest<{
      success: boolean;
      data?: {
        payment_url: string;
        payment_id: string;
        tariff: string;
        amount_rub: number;
        is_first_period: boolean;
      };
    }>(apiClient.post('/subscription/create', body));
    if (
      r.success &&
      r.data &&
      typeof r.data === 'object' &&
      (r.data as { success?: boolean }).success === true &&
      (r.data as { data?: unknown }).data
    ) {
      const d = (r.data as { data: {
        payment_url: string;
        payment_id: string;
        tariff: string;
        amount_rub: number;
        is_first_period: boolean;
      } }).data;
      return {
        success: true,
        data: {
          paymentUrl: d.payment_url,
          paymentId: d.payment_id,
          tariff: d.tariff,
          amountRub: d.amount_rub,
          isFirstPeriod: d.is_first_period,
        },
      };
    }
    return { success: false, error: r.error, data: r.data as never };
  },

  /**
   * F2: попросить сервер сверить платёж с ЮKassa (страница /paid), не дожидаясь уведомления.
   * data.data.status: succeeded | pending | canceled | failed | throttled.
   */
  async confirmPayment(paymentId: string): Promise<ApiResponse<{ success: boolean; data?: { status: string } }>> {
    return handleApiRequest(apiClient.post('/subscription/confirm', { payment_id: paymentId }));
  },

  /**
   * Отменить подписку. Опционально — причина (лог на бэке, продуктовая аналитика).
   */
  async cancelSubscription(
    cancellationReason?: string
  ): Promise<ApiResponse<{ success: boolean; message?: string; data?: unknown }>> {
    const body =
      cancellationReason && cancellationReason.trim()
        ? { cancellation_reason: cancellationReason.trim().slice(0, 200) }
        : {};
    return handleApiRequest(
      apiClient.post('/subscription/cancel', body)
    );
  },

  /**
   * Возобновить подписку
   */
  async resumeSubscription(): Promise<ApiResponse<{ success: boolean }>> {
    return handleApiRequest(
      apiClient.post('/subscription/resume')
    );
  },

  /**
   * Получить доступные тарифы
   */
  async getAvailablePlans(): Promise<ApiResponse<SubscriptionPlansPayload>> {
    return handleApiRequest<SubscriptionPlansPayload>(apiClient.get('/subscription/plans'));
  },
};

/**
 * API для профиля пользователя
 */
const profileApi = {
  /**
   * Получить профиль
   */
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    return handleApiRequest<UserProfile>(
      apiClient.get('/profile')
    );
  },

  /**
   * Обновить профиль
   */
  async updateProfile(request: UpdateProfileRequest): Promise<ApiResponse<UserProfile>> {
    return handleApiRequest<UserProfile>(
      apiClient.patch('/profile', request)
    );
  },

  /**
   * Получить прогресс
   */
  async getProgress(): Promise<ApiResponse<UserProgress>> {
    return handleApiRequest<UserProgress>(
      apiClient.get('/profile/progress')
    );
  },

  /**
   * Добавить запись веса
   */
  async addWeightEntry(weight: number, date?: string, notes?: string): Promise<ApiResponse<any>> {
    return handleApiRequest(
      apiClient.post('/profile/weight', { weight, date, notes })
    );
  },

  /**
   * Удалить аккаунт
   */
  async deleteAccount(): Promise<ApiResponse<{ success: boolean }>> {
    return handleApiRequest(
      apiClient.delete('/profile')
    );
  },
};

/**
 * API для достижений
 */
const achievementsApi = {
  /**
   * Получить все достижения
   */
  async getAchievements(): Promise<ApiResponse<any[]>> {
    return handleApiRequest(
      apiClient.get('/achievements')
    );
  },

  /**
   * Получить разблокированные достижения
   */
  async getUnlockedAchievements(): Promise<ApiResponse<any[]>> {
    return handleApiRequest(
      apiClient.get('/achievements/unlocked')
    );
  },
};

// ============= PDF-ТАРИФ (Блок F) =============

export const pdfApi = {
  /**
   * Получить информацию о специалисте по реф-коду.
   * Публичный эндпоинт — не требует авторизации.
   */
  async getSpecialistInfo(refCode: string): Promise<ApiResponse<PdfSpecialistInfo>> {
    return handleApiRequest<PdfSpecialistInfo>(
      publicApiClient.get(`/pdf/ref/${refCode.toUpperCase()}`)
    );
  },

  /**
   * Создать платёж YooKassa за PDF 1490 ₽.
   * Публичный эндпоинт — клиент открывает ссылку в браузере без Telegram-сессии.
   */
  async createPayment(data: CreatePdfPaymentRequest): Promise<CreatePdfPaymentPayload> {
    return handleApiRequest<import('../types/api').CreatePdfPaymentResponse>(
      publicApiClient.post('/pdf/create-payment', data)
    );
  },

  /**
   * Самостоятельная регистрация специалиста-партнёра.
   * Публичный эндпоинт — без авторизации.
   * Если email уже зарегистрирован — возвращает existing ref_code.
   */
  async registerSpecialist(data: RegisterSpecialistRequest): Promise<RegisterSpecialistPayload> {
    return handleApiRequest<import('../types/api').RegisterSpecialistResponse>(
      publicApiClient.post('/specialist/register', data)
    );
  },
};

// ============= EMAIL АВТОРИЗАЦИЯ =============

/**
 * API для email-авторизации (magic link)
 */
export const authApi = {
  async verifyToken(token: string): Promise<{ success: boolean; session_key?: string; user_id?: number; error?: string }> {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify/${token}`, { method: 'GET' });
      return res.json();
    } catch {
      return { success: false, error: 'network_error' };
    }
  },

  getSessionKey(): string | null {
    return localStorage.getItem('aisha_session_key');
  },

  saveSession(sessionKey: string, userId: number): void {
    localStorage.setItem('aisha_session_key', sessionKey);
    localStorage.setItem('aisha_email_user_id', String(userId));
  },

  clearSession(): void {
    localStorage.removeItem('aisha_session_key');
    localStorage.removeItem('aisha_email_user_id');
  },

  isAuthenticated(): boolean {
    return !!(window.Telegram?.WebApp?.initData || localStorage.getItem('aisha_session_key'));
  },
};

// ============= АНОНИМНАЯ АНКЕТА (quiz-funnel) =============

/** Ответ сервера после разворота handleApiRequest (код ошибки лежит в code). */
export interface QuizApiResult<T> {
  ok: boolean;
  status?: number;
  code?: string;
  data?: T;
}

export interface QuizSessionBody {
  session_id: string;
  step_idx: number;
  answers: import('../utils/quizSession').QuizAnswers;
  plan?: string | null;
  utm?: Record<string, string>;
}

async function quizRequest<T>(request: Promise<any>): Promise<QuizApiResult<T>> {
  try {
    const response = await request;
    const body = response.data;
    return { ok: body?.success !== false, status: response.status, data: body?.data as T };
  } catch (error) {
    const axiosError = error as AxiosError<any>;
    return {
      ok: false,
      status: axiosError.response?.status,
      code: axiosError.response?.data?.code,
    };
  }
}

export const quizApi = {
  /** Сохранить ответы/шаг (публично; сервер принимает только после обоих согласий). */
  saveSession(body: QuizSessionBody) {
    return quizRequest<null>(publicApiClient.post('/quiz/session', body, { timeout: 10000 }));
  },

  /** Восстановить анонимную анкету по session_id. */
  getSession(sessionId: string) {
    return quizRequest<{
      step_idx: number;
      answers: import('../utils/quizSession').QuizAnswers;
      result: import('../utils/quizSession').QuizResult | null;
      plan: string | null;
    }>(publicApiClient.get(`/quiz/session/${sessionId}`, { timeout: 10000 }));
  },

  /** Посчитать норму формулой (без ИИ) и сохранить результат в сессии. */
  calculate(body: QuizSessionBody) {
    return quizRequest<{ result: import('../utils/quizSession').QuizResult }>(
      publicApiClient.post('/quiz/calculate', body, { timeout: 15000 })
    );
  },

  /** Анкета, привязанная к аккаунту при входе по ссылке (нужен вход). */
  getClaimed() {
    return quizRequest<{
      session_id: string;
      answers: import('../utils/quizSession').QuizAnswers;
      result: import('../utils/quizSession').QuizResult | null;
      plan: string | null;
      applied: boolean;
    } | null>(apiClient.get('/quiz/claimed'));
  },

  /** По привязанной анкете решение принято — больше не спрашивать. */
  markApplied(sessionId: string) {
    return quizRequest<null>(apiClient.post('/quiz/claimed/applied', { session_id: sessionId }));
  },
};

// ============= UTM ТРЕКИНГ =============

const trackingApi = {
  /**
   * Отправить UTM-параметры первого касания на бэкенд.
   * Fire-and-forget: ошибки не блокируют UX.
   */
  async trackUtm(utmData: {
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_term?: string | null;
    utm_content?: string | null;
    referrer?: string | null;
    landing_page?: string | null;
  }): Promise<void> {
    try {
      await apiClient.post('/track/utm', utmData);
    } catch (_) {
      // не блокируем пользователя при ошибке трекинга
    }
  },
};

// ============= ЭКСПОРТ ВСЕХ API =============

const cabinetApi = {
  async getDailyTasks(): Promise<ApiResponse<any>> {
    return handleApiRequest<any>(
      apiClient.get('/cabinet/daily-tasks')
    );
  },
};

const accountApi = {
  async autoLink(emailSessionKey: string): Promise<ApiResponse<{ linked: boolean; reason?: string; email?: string }>> {
    return handleApiRequest<{ linked: boolean; reason?: string; email?: string }>(
      apiClient.post('/account/auto-link', {}, {
        headers: { 'X-Link-Session': emailSessionKey },
      })
    );
  },
};

const api = {
  onboarding: onboardingApi,
  tracking: trackingApi,
  mealPlan: mealPlanApi,
  recipe: recipeApi,
  photo: photoApi,
  foodLog: foodLogApi,
  water: waterApi,
  subscription: subscriptionApi,
  profile: profileApi,
  achievements: achievementsApi,
  pdf: pdfApi,
  cabinet: cabinetApi,
  account: accountApi,
  quiz: quizApi,
};

export default api;

// Экспортируем также отдельные API для удобства
export {
  onboardingApi,
  mealPlanApi,
  recipeApi,
  photoApi,
  foodLogApi,
  waterApi,
  subscriptionApi,
  profileApi,
  achievementsApi,
};
